import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, CircularProgress } from '@mui/material'
import PropTypes from 'prop-types'

import {
    miscItemAttachmentsApi,
    miscItemAttachmentsPath,
    useDeleteMiscItemAttachmentMutation,
    useGetMiscItemAttachmentsQuery,
    useLazyGetMiscItemAttachmentDownloadUrlQuery,
    useSetMiscItemAttachmentVisibilityMutation,
} from '@/app/services/miscItemAttachments'
import { uploadAttachmentFile } from '@/app/services/uploadAttachmentFile'

import AttachmentsPanel from './AttachmentsPanel'

/**
 * MiscItemAttachmentsPanel - The documents ledger for one misc item. Rows come from the item's
 * own list endpoint, fetched only while this panel is mounted, so a quote with many misc items issues
 * no per-item request until someone opens one. Rendering is gated by the caller.
 *
 * @param {Object} props
 * @param {string} props.organisationId
 * @param {string} props.quoteId
 * @param {string} props.miscItemId
 */
const MiscItemAttachmentsPanel = ({ miscItemId, organisationId, quoteId }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const parent = { organisationId, quoteId, miscItemId }

    // No polling: a pending row shows its scan result after a write in this panel, a reopen or a page reload.
    // Fetch on every open: a refresh that failed while the dialog was open, or an upload that
    // finished after it closed, leaves the cached list behind the server.
    const { data: attachments, isLoading } = useGetMiscItemAttachmentsQuery(parent, { refetchOnMountOrArgChange: true })

    const [setVisibility] = useSetMiscItemAttachmentVisibilityMutation()
    const [deleteAttachment, { isLoading: isDeleting }] = useDeleteMiscItemAttachmentMutation()
    const [fetchDownloadUrl] = useLazyGetMiscItemAttachmentDownloadUrlQuery()

    // The link is minted per click. A refusal (still pending, or failed) means there is nothing
    // to open, and the panel says so.
    const resolveDownloadUri = async ({ documentId }) => {
        try {
            return await fetchDownloadUrl({ ...parent, documentId }).unwrap()
        } catch {
            return undefined
        }
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress
                    aria-label={t('Loading attachments')}
                    color="secondary"
                />
            </Box>
        )
    }

    return (
        <AttachmentsPanel
            attachments={attachments ?? []}
            emptyMessage={t('No attachments on this item yet.')}
            isDeleting={isDeleting}
            organisationId={organisationId}
            resolveDownloadUri={resolveDownloadUri}
            title={t('Item attachments')}
            onDelete={(documentId) => deleteAttachment({ ...parent, documentId }).unwrap()}
            onSetVisibility={(documentId, isPublic) => setVisibility({ ...parent, documentId, isPublic }).unwrap()}
            onUploadFile={(file, options) =>
                uploadAttachmentFile({ path: miscItemAttachmentsPath(parent), file, ...options })
            }
            onUploadsSettled={() =>
                dispatch(
                    miscItemAttachmentsApi.endpoints.getMiscItemAttachments.initiate(parent, {
                        forceRefetch: true,
                        subscribe: false,
                    })
                )
            }
        />
    )
}

MiscItemAttachmentsPanel.propTypes = {
    miscItemId: PropTypes.string.isRequired,
    organisationId: PropTypes.string.isRequired,
    quoteId: PropTypes.string.isRequired,
}

export default MiscItemAttachmentsPanel
